import React, { createContext, useContext, useState, useCallback } from "react";
import { Toast } from "../components/ui/toast";

const ToastContext = createContext(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  // `raised` lifts the toasts clear of controls pinned to the foot of the
  // screen, such as free training's log box (#157).
  const addToast = useCallback(({ title, description, action, actions, duration = 5000, raised = false }) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, title, description, action, actions, duration, raised }]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Clear every toast: for a sheet or dialog opening over where they sit.
  const clearToasts = useCallback(() => setToasts((prev) => (prev.length ? [] : prev)), []);

  return (
    <ToastContext.Provider value={{ showToast: addToast, clearToasts }}>
      {children}
      
      {/* Global Toast Container */}
      {toasts.length > 0 && (
        <div
          data-toast-region=""
          className={
            toasts[toasts.length - 1].raised
              ? "fixed bottom-[calc(9rem+env(safe-area-inset-bottom,0px))] right-0 z-[100] flex max-h-screen w-full flex-col-reverse gap-2 p-4 sm:right-4 sm:max-w-[420px]"
              : "fixed bottom-0 right-0 z-[100] flex max-h-screen w-full flex-col-reverse gap-2 p-4 sm:bottom-4 sm:right-4 sm:max-w-[420px]"
          }
        >
          {toasts.map((toast) => (
            <Toast key={toast.id} {...toast} onDismiss={removeToast} />
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}
